import { useMemo } from "react";

import { Box, Flex } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import { CommonListType } from "__graphql__/types";
import { useGuardrailsFeatureFlag } from "flows/Inventory/flows/StockCorrections/hooks/useGuardrailsFeatureFlag";
import { useBarcodeScanner } from "shared/hooks/useBarcodeScanner";
import { useScan } from "shared/hooks/useScan/useScan";
import { useScrollToActiveItem } from "shared/hooks/useScrollToActiveItem";

import { InboundDroppingListItem } from "./InboundDroppingListItem";
import { InboundEndDroppingButton } from "./InboundEndDroppingButton";
import { useInboundStore } from "../stores/inboundStore/useInboundStore";
import { useAddItemToDroppingList } from "../stores/useAddItemToDroppingList";

export function InboundDroppingList() {
  const intl = useIntl();
  const translatedLabels = useMemo(
    () => ({
      singleUnitsLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.single-units-label",
      }),
      handlingUnitsLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.handling-units-label",
      }),
      inboundedLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.inbounded-label",
      }),
      totalUnitsLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.total-handling-units-label",
      }),
      bioLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.bio-label",
      }),
      nonBioLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.non-bio-label",
      }),
      outboundedLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.outbounded-label",
      }),
      outboundLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.outbound-label",
      }),
      droppingLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.dropping-label",
      }),
      inStockLabel: intl.formatMessage({
        id: "components.inbound.inbound-units-list.in-stock-label",
      }),
    }),
    [intl],
  );

  const {
    droppingListType,
    claimedProductsStockUpdates,
    claimedProductsSortedByShelf,
    inboundUIState,
    setInboundUIState,
    setInboundActiveItem,
    setSelectedProduct,
  } = useInboundStore(
    useShallow((state) => ({
      droppingListType: state.droppingListType,
      claimedProductsStockUpdates: state.claimedProductsStockUpdates,
      claimedProductsSortedByShelf: state.claimedProductsSortedByShelf,
      inboundUIState: state.inboundUIState,
      setInboundUIState: state.setInboundUIState,
      setInboundActiveItem: state.setInboundActiveItem,
      setSelectedProduct: state.setSelectedProduct,
    })),
  );
  const { activeItemId, isScrollToActiveItem } = inboundUIState;
  const { fetchProductByEan } = useAddItemToDroppingList();
  const { processBarcode } = useBarcodeScanner();
  const productMapBySku = new Map(claimedProductsSortedByShelf.map((p) => [p.sku, p]));
  const productMapByEan = new Map();
  claimedProductsSortedByShelf.forEach((p) => p.eans.forEach((ean) => productMapByEan.set(ean, p)));

  const handleScannedItem = async (scannedValue: string) => {
    const ean = processBarcode(scannedValue);
    if (!ean) return;
    if (isScrollToActiveItem) return;

    const foundProduct = productMapByEan.get(ean);

    if (foundProduct) {
      const isAlreadyInbounded = !!claimedProductsStockUpdates[foundProduct.sku]?.stockUpdated;
      if (isAlreadyInbounded) {
        setInboundUIState({ isProductAlreadyInbounded: true });
      }
      setInboundActiveItem(foundProduct.sku, true);
      return;
    }

    const fetchedProduct = await fetchProductByEan(ean);
    if (!fetchedProduct) {
      setInboundUIState({ isScanningFailedModalVisible: true });
      return;
    }

    const skuFound = productMapBySku.get(fetchedProduct.sku);
    if (!skuFound) {
      setInboundUIState({ isProductNotInDroppingList: true });
      setSelectedProduct(fetchedProduct);
      return;
    }

    const isAlreadyInbounded = !!claimedProductsStockUpdates[fetchedProduct.sku]?.stockUpdated;
    if (isAlreadyInbounded) {
      setInboundUIState({ isProductAlreadyInbounded: true });
    }

    setInboundActiveItem(fetchedProduct.sku, true);
  };

  const processScannedItem = async (scannedValue: string) => {
    const ean = processBarcode(scannedValue);
    if (!ean) return;

    const foundProduct = productMapByEan.get(ean);
    if (foundProduct) {
      setInboundActiveItem(foundProduct.sku, true);
      return;
    }

    const fetchedProduct = await fetchProductByEan(ean);
    if (!fetchedProduct) return;

    const skuFound = productMapBySku.get(fetchedProduct.sku);
    if (skuFound) {
      setInboundActiveItem(skuFound.sku, true);
    }
  };

  const isSearchFlowActive = droppingListType !== CommonListType.list_type_cancelled_order;

  useScan({
    onScan: isSearchFlowActive ? handleScannedItem : processScannedItem,
  });

  const droppingListInboundUnitsSkusSortedByShelf = useMemo(
    () => claimedProductsSortedByShelf.map((p) => p.sku),
    [claimedProductsSortedByShelf],
  );

  const isDroppingComplete = useMemo(
    () =>
      Object.values(claimedProductsStockUpdates).every(
        (stockUpdateState) => !!stockUpdateState.stockUpdated,
      ),
    [claimedProductsStockUpdates],
  );

  const { itemsRef, scrollContainerRef } = useScrollToActiveItem({
    activeItemId,
    listItemIds: droppingListInboundUnitsSkusSortedByShelf,
    isScrollToActiveItem,
    setInboundUIState,
  });

  const guardrails = useGuardrailsFeatureFlag();
  const deviationThreshold = guardrails?.deviationThreshold;
  const quantityChangeLimit = guardrails?.quantityChangeLimit;

  return (
    <Flex
      direction="column"
      pos="relative"
      ref={scrollContainerRef}
      gap="s150"
      flex={1}
      overflowY="scroll"
      w="100%"
      pt="s200"
    >
      {droppingListInboundUnitsSkusSortedByShelf.map((sku, index) => (
        <InboundDroppingListItem
          key={sku}
          sku={sku}
          elementRef={itemsRef.current[index]}
          translatedLabels={translatedLabels}
          deviationThreshold={deviationThreshold}
          quantityChangeLimit={quantityChangeLimit}
        />
      ))}
      {/* Below box helps us having the EndDroppingProcessButton not overflowing bottom content */}
      <Box mb="60px" />
      {isDroppingComplete && <InboundEndDroppingButton />}
    </Flex>
  );
}
